import { useEffect, useState, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { fetchShopifyProducts, fetchProductsByCollection } from "@/lib/shopify";
import type { ShopifyProduct } from "@/lib/shopify";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import logoAlice from "@/assets/logo-alice-new.png";
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, Snowflake, Wind, Thermometer, CloudSun, Moon, CloudMoon } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

interface TVSettings {
  collection_handles: string[];
  promo_messages: string[];
  slide_duration_seconds: number;
  is_active: boolean;
  show_price: boolean;
  show_variants: boolean;
  sale_is_active: boolean;
  sale_collection_handle: string;
  sale_label: string;
  sale_end_time: string;
  sale_badge_color: string;
}

const DEFAULT_SETTINGS: TVSettings = {
  collection_handles: [],
  promo_messages: [],
  slide_duration_seconds: 30,
  is_active: true,
  show_price: true,
  show_variants: true,
  sale_is_active: false,
  sale_collection_handle: "",
  sale_label: "מבצע!",
  sale_end_time: "",
  sale_badge_color: "#c0392b",
};

interface GoldPrices {
  gold: { per_oz: number; per_gram_24k: number; per_gram_18k: number; per_gram_14k: number };
  silver: { per_oz: number; per_gram: number };
  usd_to_ils: number;
  timestamp: string;
}

// ─── Historical Gold Price Chart ─────────────────────────────────────────
const goldHistoryData = [
  { year: "2022", price: 5900 },
  { year: "2023", price: 6400 },
  { year: "2024", price: 7800 },
  { year: "2025", price: 9200 },
  { year: "2026", price: 9700 },
];

const GoldHistoryChart = () => {
  return (
    <div className="w-full flex-1 flex flex-col min-h-0">
      <p className="text-xs text-center mb-1" style={{ color: "rgba(137,124,91,0.6)" }}>
        מחיר זהב לאונקיה (₪) • 4 שנים
      </p>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={goldHistoryData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#897C5B" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#897C5B" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: "#897C5B" }}
              axisLine={{ stroke: "rgba(137,124,91,0.15)" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "rgba(137,124,91,0.5)" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `₪${v}`}
            />
            <Tooltip
              formatter={(value: number) => [`₪${value}`, "מחיר"]}
              contentStyle={{
                background: "#fff",
                border: "1px solid rgba(137,124,91,0.2)",
                borderRadius: 0,
                fontSize: "12px",
                color: "#897C5B",
              }}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke="#897C5B"
              strokeWidth={2}
              fill="url(#goldGrad)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─── Sale Countdown ─────────────────────────────────────────
const SaleCountdown = ({ endTime, label, color }: { endTime: string; label: string; color: string }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const update = () => {
      const end = new Date(endTime).getTime();
      const now = Date.now();
      const diff = end - now;

      if (diff <= 0) {
        setExpired(true);
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  if (expired) return null;

  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <div
      className="flex items-center justify-center gap-6 px-6 py-3"
      style={{ color: "#897C5B" }}
    >
      <div
        className="h-px flex-1"
        style={{ background: "linear-gradient(90deg, transparent, rgba(137,124,91,0.3), transparent)" }}
      />
      <span className="text-lg font-semibold" style={{ color: "#897C5B" }}>{label}</span>
      <div className="flex items-center gap-1 text-2xl font-mono font-bold" dir="ltr" style={{ color: "#897C5B" }}>
        {timeLeft.days > 0 && (
          <>
            <span>{timeLeft.days}</span>
            <span className="text-sm font-normal opacity-60 mx-1">ימים</span>
          </>
        )}
        <span>{pad(timeLeft.hours)}</span>
        <span className="opacity-40">:</span>
        <span>{pad(timeLeft.minutes)}</span>
        <span className="opacity-40">:</span>
        <span>{pad(timeLeft.seconds)}</span>
      </div>
      <div
        className="h-px flex-1"
        style={{ background: "linear-gradient(90deg, transparent, rgba(137,124,91,0.3), transparent)" }}
      />
    </div>
  );
};

const getWeatherIcon = (code: number, isDay: boolean) => {
  const props = { size: 22, color: "#897C5B", strokeWidth: 1.8 };
  if (code === 0) return isDay ? <Sun {...props} /> : <Moon {...props} />;
  if (code <= 2) return isDay ? <CloudSun {...props} /> : <CloudMoon {...props} />;
  if (code === 3) return <Cloud {...props} />;
  if (code <= 49) return <Cloud {...props} />;
  if (code <= 59) return <CloudDrizzle {...props} />;
  if (code <= 69) return <CloudRain {...props} />;
  if (code <= 79) return <Snowflake {...props} />;
  if (code <= 84) return <CloudRain {...props} />;
  if (code <= 86) return <CloudSnow {...props} />;
  if (code <= 99) return <CloudLightning {...props} />;
  return <Thermometer {...props} />;
};

const getWeatherLabel = (code: number): string => {
  if (code === 0) return "בהיר";
  if (code <= 2) return "מעונן חלקית";
  if (code === 3) return "מעונן";
  if (code <= 49) return "ערפל";
  if (code <= 59) return "טפטוף";
  if (code <= 69) return "גשם";
  if (code <= 79) return "שלג";
  if (code <= 84) return "גשם חזק";
  if (code <= 86) return "שלג חזק";
  if (code <= 99) return "סופת רעמים";
  return "";
};

const LiveWeather = () => {
  const [temp, setTemp] = useState<number | null>(null);
  const [weatherCode, setWeatherCode] = useState<number>(0);
  const [isDay, setIsDay] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=31.25&longitude=34.79&current=temperature_2m,weather_code,is_day"
        );
        const data = await res.json();
        if (data.current) {
          setTemp(Math.round(data.current.temperature_2m));
          setWeatherCode(data.current.weather_code);
          setIsDay(data.current.is_day === 1);
        }
      } catch (err) {
        console.error("Weather fetch error:", err);
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (temp === null) return null;

  return (
    <span className="flex items-center gap-2 text-2xl font-light" style={{ color: "#897C5B" }}>
      {getWeatherIcon(weatherCode, isDay)}
      <span>{temp}°C</span>
      <span>{getWeatherLabel(weatherCode)}</span>
    </span>
  );
};

// ─── Clock ─────────────────────────────────────────
const LiveClock = () => {
  const [time, setTime] = useState("");
  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const hours = now.getHours();
      const timeStr = now.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      setTime(timeStr);

      if (hours >= 5 && hours < 12) setGreeting("שלום ובוקר טוב,");
      else if (hours >= 12 && hours < 17) setGreeting("שלום וצהריים טובים,");
      else if (hours >= 17 && hours < 21) setGreeting("שלום וערב טוב,");
      else setGreeting("שלום ולילה טוב,");
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="text-2xl font-light" style={{ color: "#897C5B" }}>
      {greeting} {time}
    </span>
  );
};

// ─── News Ticker ─────────────────────────────────────────
interface NewsItem {
  title: string;
  link: string;
  pubDate: string;
}

const NewsTicker = ({ items }: { items: NewsItem[] }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    // Save original HTML, then duplicate for seamless loop
    const originalHTML = inner.innerHTML;
    inner.innerHTML = Array(20).fill(originalHTML).join("");

    const singleWidth = inner.scrollWidth / 20;
    let pos = 0;
    let animId: number;

    const step = () => {
      pos += 1.2;
      if (pos >= singleWidth) pos = 0;
      inner.style.transform = `translateX(${pos}px)`;
      animId = requestAnimationFrame(step);
    };

    animId = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(animId);
      inner.innerHTML = originalHTML;
    };
  }, [items]);

  return (
    <div
      ref={scrollRef}
      className="w-full shrink-0 overflow-hidden relative flex"
      style={{ height: "40px", background: "#f3f2ee", marginTop: "4px", border: "1px solid #897c5b" }}
    >
      {/* Fixed label on the right */}
      <div
        className="shrink-0 flex items-center justify-center px-5 z-10"
        style={{ background: "#897c5b", color: "#fff", fontSize: "13px", fontWeight: 600, letterSpacing: "0.5px" }}
      >
        חדשות
      </div>
      {/* Scrolling ticker */}
      <div className="flex-1 overflow-hidden relative">
        <div className="absolute inset-0 flex items-center overflow-hidden">
          <div ref={innerRef} className="inline-flex items-center whitespace-nowrap" style={{ direction: "rtl" }}>
            {items.map((item, i) => (
              <span key={i} className="text-sm inline-flex items-center" style={{ color: "#897c5b" }}>
                <span className="mx-5 text-lg" style={{ color: "rgba(137,124,91,0.5)" }}>●</span>
                {item.title}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main TV Display ─────────────────────────────────────────────
const TVDisplay = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<TVSettings>(DEFAULT_SETTINGS);
  const [goldPrices, setGoldPrices] = useState<GoldPrices | null>(null);
  const [promoIndex, setPromoIndex] = useState(0);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [saleProductIds, setSaleProductIds] = useState<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load gold prices
  useEffect(() => {
    const fetchGoldPrices = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("gold-prices");
        if (!error && data) setGoldPrices(data);
      } catch (err) {
        console.error("Error fetching gold prices:", err);
      }
    };
    fetchGoldPrices();
    const interval = setInterval(fetchGoldPrices, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Load Maariv RSS news
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("maariv-rss");
        if (!error && data?.items) setNewsItems(data.items);
      } catch (err) {
        console.error("Error fetching news:", err);
      }
    };
    fetchNews();
    const interval = setInterval(fetchNews, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from("tv_display_settings")
          .select("*")
          .eq("id", "default")
          .single();

        if (!error && data) {
          setSettings({
            collection_handles: data.collection_handles || [],
            promo_messages: data.promo_messages || [],
            slide_duration_seconds: data.slide_duration_seconds || 30,
            is_active: data.is_active ?? true,
            show_price: data.show_price ?? true,
            show_variants: data.show_variants ?? true,
            sale_is_active: (data as any).sale_is_active ?? false,
            sale_collection_handle: (data as any).sale_collection_handle || "",
            sale_label: (data as any).sale_label || "מבצע!",
            sale_end_time: (data as any).sale_end_time || "",
            sale_badge_color: (data as any).sale_badge_color || "#c0392b",
          });
          setSettingsLoaded(true);
        }
      } catch (err) {
        console.error("Error loading TV settings:", err);
      }
    };

    loadSettings();

    const channel = supabase
      .channel("tv-settings")
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "tv_display_settings",
        filter: "id=eq.default",
      }, (payload) => {
        const d = payload.new as any;
        setSettings({
          collection_handles: d.collection_handles || [],
          promo_messages: d.promo_messages || [],
          slide_duration_seconds: d.slide_duration_seconds || 30,
          is_active: d.is_active ?? true,
          show_price: d.show_price ?? true,
          show_variants: d.show_variants ?? true,
          sale_is_active: d.sale_is_active ?? false,
          sale_collection_handle: d.sale_collection_handle || "",
          sale_label: d.sale_label || "מבצע!",
          sale_end_time: d.sale_end_time || "",
          sale_badge_color: d.sale_badge_color || "#c0392b",
        });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Load products - only after settings are loaded, use serialized handles to detect changes
  const collectionHandlesKey = JSON.stringify(settings.collection_handles);
  useEffect(() => {
    if (!settingsLoaded) return;
    const loadProducts = async () => {
      // Only show loading on initial load, not on live updates
      if (products.length === 0) setIsLoading(true);
      let allProducts: ShopifyProduct[] = [];

      try {
        const handles = JSON.parse(collectionHandlesKey) as string[];
        if (handles.length > 0) {
          for (const handle of handles) {
            let cursor: string | undefined;
            let hasMore = true;
            while (hasMore) {
              const { edges, pageInfo } = await fetchProductsByCollection(handle, 50, cursor);
              allProducts = [...allProducts, ...edges.map(e => e.node)];
              hasMore = pageInfo.hasNextPage;
              cursor = pageInfo.endCursor || undefined;
            }
          }
          const seen = new Set<string>();
          allProducts = allProducts.filter(p => {
            if (seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
          });
        } else {
          let cursor: string | undefined;
          let hasMore = true;
          while (hasMore) {
            const { edges, pageInfo } = await fetchShopifyProducts(50, cursor);
            allProducts = [...allProducts, ...edges.map(e => e.node)];
            hasMore = pageInfo.hasNextPage;
            cursor = pageInfo.endCursor || undefined;
          }
        }
        setProducts(allProducts.filter(p => p.images.edges.length > 0));
        setCurrentIndex(0);
      } catch (error) {
        console.error("Error loading products:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducts();
  }, [collectionHandlesKey, settingsLoaded]);

  // Load sale collection product IDs
  useEffect(() => {
    if (!settings.sale_is_active || !settings.sale_collection_handle) {
      setSaleProductIds(new Set());
      return;
    }
    const loadSaleProducts = async () => {
      try {
        const ids = new Set<string>();
        let cursor: string | undefined;
        let hasMore = true;
        while (hasMore) {
          const { edges, pageInfo } = await fetchProductsByCollection(settings.sale_collection_handle, 50, cursor);
          edges.forEach(e => ids.add(e.node.id));
          hasMore = pageInfo.hasNextPage;
          cursor = pageInfo.endCursor || undefined;
        }
        setSaleProductIds(ids);
      } catch (error) {
        console.error("Error loading sale products:", error);
      }
    };
    loadSaleProducts();
  }, [settings.sale_is_active, settings.sale_collection_handle]);

  // Auto-cycle products
  useEffect(() => {
    if (products.length === 0) return;
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % products.length);
    }, settings.slide_duration_seconds * 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [products.length, settings.slide_duration_seconds]);

  // Auto-cycle promo messages
  useEffect(() => {
    if (settings.promo_messages.length <= 1) return;
    const interval = setInterval(() => {
      setPromoIndex(prev => (prev + 1) % settings.promo_messages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [settings.promo_messages.length]);

  const getProductImages = useCallback((p: ShopifyProduct): string[] => {
    const urls = new Set<string>();
    p.images.edges.forEach(img => urls.add(img.node.url));
    p.variants.edges.forEach(v => {
      if (v.node.image?.url) urls.add(v.node.image.url);
    });
    return Array.from(urls);
  }, []);

  const goToPrevPromo = () => {
    if (settings.promo_messages.length <= 1) return;
    setPromoIndex(prev => (prev - 1 + settings.promo_messages.length) % settings.promo_messages.length);
  };

  const goToNextPromo = () => {
    if (settings.promo_messages.length <= 1) return;
    setPromoIndex(prev => (prev + 1) % settings.promo_messages.length);
  };

  // ─── Static screens ─────────────────────────────
  if (!settings.is_active) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <img src={logoAlice} alt="Alice Jewelry" className="h-20 opacity-20" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Alice Jewelry - תצוגת חנות</title>
        </Helmet>
        <div className="text-center">
          <img src={logoAlice} alt="Alice Jewelry" className="h-16 mx-auto mb-8 opacity-60" />
          <div className="gold-bar-loader" />
          <p className="text-sm mt-4" style={{ color: "#897C5B" }}>טוען מסך תצוגה...</p>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="text-center">
          <img src={logoAlice} alt="Alice Jewelry" className="h-16 mx-auto mb-6 opacity-40" />
          <p style={{ color: "#897C5B" }}>אין מוצרים להצגה</p>
          <p className="text-sm mt-2" style={{ color: "rgba(137,124,91,0.5)" }}>הגדר קטגוריות בפאנל הניהול</p>
        </div>
      </div>
    );
  }

  const product = products[currentIndex];
  const images = getProductImages(product);
  const hasPromo = settings.promo_messages.length > 0;

  return (
    <div className="fixed inset-0 bg-white overflow-hidden" dir="rtl">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Alice Jewelry - תצוגת חנות</title>
      </Helmet>

      <div className="w-full h-full flex flex-col" style={{ padding: "23px 25px" }}>
        {/* ═══ Top Header Bar ═══ */}
        <div
          className="w-full flex items-center justify-between px-8 shrink-0"
          style={{
            height: "67px",
            background: "rgba(137, 124, 91, 0.1)",
          }}
        >
          {/* Right: Logo */}
          <img src={logoAlice} alt="Alice Jewelry" className="h-[45px] object-contain" />

          {/* Left: Clock + separator + Weather */}
          <div className="flex items-center gap-4">
            <LiveClock />
            <div style={{ width: "1px", height: "24px", background: "rgba(137,124,91,0.3)" }} />
            <LiveWeather />
          </div>
        </div>

        {/* ═══ Main Content Area ═══ */}
        <div className="flex flex-1 mt-[27px] gap-0 min-h-0 overflow-hidden">

          {/* Left Sidebar - Gold News */}
          <div
            className="shrink-0 flex flex-col"
            style={{
              width: "336px",
              border: "1px solid rgba(137, 124, 91, 0.1)",
              background: "#FFFFFF",
            }}
          >
            <div className="py-4 px-6">
              <h2
                className="text-2xl font-bold text-center"
                style={{ color: "#897C5B" }}
              >
                חדשות זהב
              </h2>
            </div>
            <div style={{ borderBottom: "1px solid rgba(137, 124, 91, 0.1)" }} />

            {/* Gold prices details */}
            <div className="flex-1 p-4 flex flex-col gap-4 overflow-hidden">
              {goldPrices ? (
                <>
                  <div className="space-y-0">
                    {[
                      { label: "זהב 24K", value: `₪${goldPrices.gold.per_oz.toLocaleString("he-IL")} / אונקייה` },
                      { label: "זהב 18K", value: `₪${Math.round(goldPrices.gold.per_oz * 0.75).toLocaleString("he-IL")} / אונקייה` },
                      { label: "זהב 14K", value: `₪${Math.round(goldPrices.gold.per_oz * 0.585).toLocaleString("he-IL")} / אונקייה` },
                      { label: "כסף", value: `₪${goldPrices.silver.per_oz.toLocaleString("he-IL")} / אונקייה` },
                      { label: "דולר / שקל", value: `₪${goldPrices.usd_to_ils.toFixed(2)}` },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="flex items-center justify-between py-2 px-3"
                        style={{ borderBottom: "1px solid rgba(137, 124, 91, 0.08)" }}
                      >
                        <span className="text-sm" style={{ color: "#897C5B" }}>{item.label}</span>
                        <span className="text-sm font-semibold" style={{ color: "#897C5B" }}>{item.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Historical Chart */}
                  <GoldHistoryChart />
                </>
              ) : (
                <div className="flex items-center justify-center flex-1">
                  <p className="text-sm" style={{ color: "rgba(137,124,91,0.4)" }}>טוען מחירים...</p>
                </div>
              )}
            </div>
          </div>

          {/* Middle - Product Description */}
          <AnimatePresence mode="wait">
            <motion.div
              key={product.id + "-desc"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="shrink-0 overflow-hidden p-6 flex flex-col items-center justify-center text-center"
              style={{
                width: "507px",
                background: "rgba(137, 124, 91, 0.1)",
              }}
            >
              <h2
                className="text-3xl font-semibold mb-3"
                style={{ color: "#897C5B" }}
              >
                {product.title}
              </h2>
              {settings.show_price && product.variants.edges.length > 0 && (
                <p
                  className="text-2xl font-bold mb-4"
                  style={{ color: "#897C5B" }}
                >
                  ₪{Math.round(parseFloat(product.variants.edges[0].node.price?.amount || "0")).toLocaleString("he-IL")}
                </p>
              )}
              {product.descriptionHtml ? (
                <div
                  className="text-sm font-normal leading-relaxed prose prose-sm max-w-none"
                  style={{ color: "#897C5B" }}
                  dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                />
              ) : product.description ? (
                <p
                  className="text-sm font-normal leading-relaxed"
                  style={{ color: "#897C5B" }}
                >
                  {product.description}
                </p>
              ) : null}

              {/* Sale countdown inside product card */}
              {settings.sale_is_active && settings.sale_end_time && saleProductIds.has(product.id) && (
                <div className="w-full mt-4">
                  <SaleCountdown
                    endTime={settings.sale_end_time}
                    label={settings.sale_label}
                    color={settings.sale_badge_color}
                  />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Right - Product Image */}
          <div
            className="flex-1 flex items-center justify-center relative"
            style={{
              border: "1px solid rgba(137, 124, 91, 0.1)",
              background: "#FFFFFF",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={product.id + "-img"}
                src={images[0] + "&width=1200"}
                alt={product.title}
                className="max-h-[80%] max-w-[80%] object-contain"
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </AnimatePresence>
          </div>
        </div>




        {/* ═══ Bottom Promo Bar ═══ */}
        <div
          className="w-full shrink-0 flex items-center justify-between px-4 mt-[27px]"
          style={{
            height: "67px",
            background: "#897C5B",
          }}
        >
          {/* Promo message */}
          <div className="w-full text-center overflow-hidden">
            {hasPromo ? (
              <AnimatePresence mode="wait">
                <motion.p
                  key={promoIndex}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="text-lg font-normal text-white"
                >
                  {settings.promo_messages[promoIndex]}
                </motion.p>
              </AnimatePresence>
            ) : (
              <p className="text-lg font-normal text-white">
                לקוחות יקרים ניתן לעבור למעקב אחר הזמנות ותיקונים מכאן
              </p>
            )}
          </div>
        </div>

        {/* ═══ Maariv News Ticker ═══ */}
        {newsItems.length > 0 && <NewsTicker items={newsItems} />}
      </div>
    </div>
  );
};

export default TVDisplay;